import {
  assoc, isEmpty, compose, not, isNil, map, reduce, mergeRight, sortBy,
  groupBy, prop, values,
} from 'ramda';
import to from 'await-to-js';
import {
  splitId, splitConversationId, isGroup, parseApiConversation, cleanConversationObject,
  CONVERSATION_TYPE, CONVERSATION_EXPOSURE,
} from '@twnel/utils-js/web';
import { getSelectedCompanyId, getUserAgentId } from '@twnel/companies-login';
import { CONVERSATIONS } from 'src/data/constants';
import { getConversation, getMessage } from 'src/data/selectors';
import {
  conversationNotification, shouldNotifyMessage, messageNotification,
} from 'src/data/util';
import { updateConversations } from '../basic';
import { fetchConversationsOrigin } from './contacts';
import { archiveConversation, cleanAndFilter } from './util';

const {
  AGENT, BUSINESS,
} = CONVERSATION_TYPE;
const {
  ACTIVE, PENDING, INACTIVE,
} = CONVERSATION_EXPOSURE;

export const onSocketConversations = (rawConversations = []) => async (
  dispatch,
  getState,
  getContext,
) => {
  let conversations = await dispatch(fetchConversationsOrigin(
    compose(
      values,
      map(compose(
        reduce(mergeRight, {}),
        sortBy(prop('updated_at')),
      )),
      groupBy(prop('with')),
      map((rawConversation) => {
        const conversation = parseApiConversation(rawConversation);
        if (!Object.prototype.hasOwnProperty.call(rawConversation, 'exposure')) {
          delete conversation.exposure;
        }
        return conversation;
      }),
    )(rawConversations),
    { ignoreCache: false },
  )).then(cleanAndFilter(getState()));

  if (isEmpty(conversations)) {
    return;
  }

  const userAgentId = getUserAgentId(getState());
  const companyId = getSelectedCompanyId(getState());
  conversations = conversations.map((conversation) => {
    const existing = getConversation(companyId, conversation.id, getState());
    if (!existing && (
      !Object.prototype.hasOwnProperty.call(conversation, 'exposure')
        || conversation.exposure === INACTIVE
    )) {
      return null;
    }

    // Conversation should exist or else the previous return should've triggered
    let update = conversation;
    if (!Object.prototype.hasOwnProperty.call(conversation, 'exposure')) {
      update = { ...update, exposure: existing.exposure };
    }

    if (
      (existing?.exposure !== PENDING && update.exposure === PENDING)
      || (
        (existing?.exposure !== ACTIVE && existing?.agent?.id !== userAgentId)
        && (update.exposure === ACTIVE && update.agent?.id === userAgentId)
      )
    ) {
      const { notificationsManager } = getContext();
      const notification = conversationNotification(update, getState());
      notificationsManager.sendNotification(notification);
    }

    const { lastMessage: message } = update;
    const existingMessage = getMessage(companyId, update.id, message?.id, getState());
    if (
      message && !existingMessage?.notified
      && shouldNotifyMessage(update, message, getState())
    ) {
      const { notificationsManager } = getContext();
      const notification = messageNotification(update, message, getState());
      notificationsManager.sendNotification(notification);
      return {
        ...update,
        lastMessage: {
          ...message,
          notified: true,
        },
      };
    }
    return update;
  }).filter(compose(not, isNil));

  if (isEmpty(conversations)) {
    return;
  }

  dispatch(updateConversations(companyId, conversations));
};

export const onTypingEvent = (rawId) => (dispatch, getState, getContext) => {
  const { id: conversationId, agent } = splitId(rawId);
  if (agent) {
    // Impossible to identify the conversation without the owner id.
    return;
  }

  const companyId = getSelectedCompanyId(getState());
  const conversation = () => getConversation(companyId, conversationId, getState());
  if (!conversation()) {
    return;
  }

  const updateTyping = (typing) => {
    const instance = conversation();
    if (!instance) return;
    dispatch(updateConversations(companyId, [
      assoc('typing', typing, instance),
    ]));
  };

  const { cacheStore } = getContext();
  const typingTimeouts = cacheStore.get(`${CONVERSATIONS}/typingTimeouts`);

  updateTyping(true);
  clearTimeout(typingTimeouts.get(conversationId));
  typingTimeouts.set(conversationId, setTimeout(() => {
    typingTimeouts.delete(conversationId);
    updateTyping(false);
  }, 3000));
};

export const freeConversation = (conversationId) => async (dispatch, getState, getContext) => {
  const companyId = getSelectedCompanyId(getState());
  const conversation = getConversation(companyId, conversationId, getState());
  if (!conversation) {
    throw Error('Conversation not found in memory');
  }

  if (isGroup(conversation) || conversation.exposure === PENDING) {
    return;
  }

  const { exposure: originalExposure } = conversation;
  dispatch(updateConversations(companyId, [
    assoc('exposure', PENDING, conversation),
  ]));

  const { socket } = getContext();
  try {
    await socket.emit('free', {
      ...cleanConversationObject(conversation),
      exposure: conversation.exposure,
    });
  } catch (error) {
    const updatedConversation = getConversation(companyId, conversationId, getState());
    dispatch(updateConversations(companyId, [
      assoc('exposure', originalExposure, updatedConversation),
    ]));
  }
};

export const lockConversation = (conversationId, agentId) => async (
  dispatch,
  getState,
  getContext,
) => {
  const companyId = getSelectedCompanyId(getState());
  const conversation = getConversation(companyId, conversationId, getState());
  if (!conversation) {
    throw Error('Conversation not found in memory');
  }

  if (
    isGroup(conversation)
    || (conversation.exposure === ACTIVE && (!agentId || conversation.agent?.id === agentId))
  ) {
    return true;
  }

  const { api, socket, cacheStore } = getContext();
  const lockRequests = cacheStore.get(`${CONVERSATIONS}/lockRequests`);
  let promise = lockRequests.get(conversationId);
  if (promise) {
    return promise;
  }

  const assignto = agentId ?? getUserAgentId(getState());
  const lockAttempt = () => socket.emit('lock', {
    ...cleanConversationObject(conversation),
    exposure: conversation.exposure,
    agent: { id: assignto },
  });

  promise = (async function lock() {
    let [error] = await to(lockAttempt());
    if (/^conversation not found$/i.test(error)) {
      [error] = await to(api.conversations.update([conversation]));
      if (!error) {
        [error] = await to(lockAttempt());
      }
    }
    lockRequests.delete(conversationId);
    if (error) {
      throw error;
    }
  }());

  lockRequests.set(conversationId, promise);
  return promise;
};

export const closeConversation = (conversationId) => async (dispatch, getState, getContext) => {
  const selectedCompanyId = getSelectedCompanyId(getState());
  const conversation = getConversation(selectedCompanyId, conversationId, getState());
  if (!conversation) {
    throw Error('Conversation not found in memory');
  }

  if (isGroup(conversation) || conversation.exposure === INACTIVE) {
    return;
  }

  let remove;
  if (conversation.type === AGENT) {
    const { id: companyId } = splitConversationId(conversation);
    remove = selectedCompanyId === companyId;
  } else {
    remove = conversation.type === BUSINESS;
  }

  const archivedConversation = archiveConversation(getState(), conversation, { remove });
  dispatch(updateConversations(selectedCompanyId, [archivedConversation]));

  const conversationData = {
    ...cleanConversationObject(conversation),
    exposure: conversation.exposure,
  };

  const { topic_selected: topicSelected } = conversation.data || {};
  if (topicSelected) {
    conversationData.data = assoc(
      'topic_selected',
      topicSelected,
      conversationData.data,
    );
  }

  const { socket } = getContext();
  try {
    await socket.emit('close', conversationData);
  } catch (error) {
    dispatch(updateConversations(selectedCompanyId, [conversation]));
  }
};
